// Answer-area inputs for the launch kinds (boards 14 N1/N5/N7–N9, 17 M3/M5/M6, 18 A7): the keypad, the
// number field, digit slots, the ordered-tap counter and Yes/No rows. Keyboard: the field and the slots
// take digits, "-" and Backspace; keypad keys are buttons; Yes/No are toggle buttons (Y / N keys too).
import { useId, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import type { Board, GridBoard } from "../types";
import { kindOf } from "../types";
import styles from "./launch.module.css";
import type { LaunchPlay } from "./play";

const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).join(" ");

function EraseIcon() {
  return (
    <svg viewBox="0 0 24 24" className={styles.icon} aria-hidden>
      <path d="M9 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-6-7 6-7z" fill="none" strokeWidth={1.8} strokeLinejoin="round" />
      <path d="M12 9.5l5 5M17 9.5l-5 5" fill="none" strokeWidth={1.8} strokeLinecap="round" />
    </svg>
  );
}

/**
 * The keypad (board 14): big rounded keys in rows of five. `erase` puts ⌫ in the grid ("inline", after the
 * digits, as N8/N9) or as a tall key spanning both rows at the end ("tall", when all ten digits are needed).
 * In previews it is drawn but inert (hidden from assistive tech: there is nothing to press).
 */
export function Keypad({
  keys,
  play,
  erase = "none",
  minus = false,
  label = "Keypad",
}: {
  keys: number[];
  play: LaunchPlay;
  erase?: "none" | "inline" | "tall";
  minus?: boolean;
  label?: string;
}) {
  const live = play.interactive && !play.locked;
  const inline = erase === "inline";
  const count = keys.length + (inline ? 1 : 0) + (minus ? 1 : 0);
  const columns = erase === "tall" ? 6 : Math.min(5, Math.max(1, count));
  const key = (content: ReactNode, onPress: () => void, extra: { label?: string; className?: string; style?: CSSProperties; data: string }) =>
    play.interactive ? (
      <button
        key={extra.data}
        type="button"
        className={cx(styles.key, extra.className)}
        style={extra.style}
        aria-label={extra.label}
        aria-disabled={!live || undefined}
        data-key={extra.data}
        onClick={() => live && onPress()}
      >
        {content}
      </button>
    ) : (
      <span key={extra.data} className={cx(styles.key, extra.className)} style={extra.style} data-key={extra.data}>
        {content}
      </span>
    );
  return (
    <div
      className={styles.keypad}
      role={play.interactive ? "group" : undefined}
      aria-label={play.interactive ? label : undefined}
      aria-hidden={play.interactive ? undefined : true}
      data-disabled={!live || undefined}
      style={{ "--key-cols": columns } as CSSProperties}
    >
      {keys.map((d) => key(String(d), () => play.type(d), { data: String(d) }))}
      {minus ? key("−", () => play.type("-"), { label: "Minus", data: "-" }) : null}
      {erase !== "none"
        ? key(<EraseIcon />, () => play.erase(), {
            label: "Delete",
            className: styles.keyErase,
            data: "erase",
            style: erase === "tall" ? { gridColumn: "6", gridRow: "1 / span 2" } : undefined,
          })
        : null}
    </div>
  );
}

const DIGIT_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

/** Digits, "-" and Backspace/Delete typed on a focused field, slots or blank. Returns true when handled. */
export function handleTypingKey(e: KeyboardEvent, play: LaunchPlay, allowMinus: boolean): boolean {
  if (e.metaKey || e.ctrlKey || e.altKey) return false;
  if (/^[0-9]$/.test(e.key)) {
    play.type(Number(e.key));
    return true;
  }
  if (allowMinus && (e.key === "-" || e.key === "−")) {
    play.type("-");
    return true;
  }
  if (e.key === "Backspace" || e.key === "Delete") {
    play.erase();
    return true;
  }
  return false;
}

/**
 * answer.number@1 (board 14 N1/N5): the "Answer" field with the typed number, an accent caret, ⌫ inside
 * it, then the ten-key pad. The field turns accent once something is typed; revealed, it shows the correct
 * number (and the player's wrong one beneath it).
 */
function NumberInput({ play }: { play: LaunchPlay }) {
  const hintId = useId();
  const allowMinus = Boolean((play.spec as { allowNegative?: boolean }).allowNegative);
  const shownText = play.shown?.kind === "number" ? String(play.shown.value ?? "") : "";
  const value = play.revealed ? shownText : play.text;
  const wrong = play.revealed && play.interactive && play.text !== "" && play.text.replace(/^(-?)0+(?=\d)/, "$1") !== shownText;
  const state = play.revealed ? "correct" : play.text ? "typing" : "empty";
  const live = play.interactive && !play.locked;
  return (
    <div className={styles.answer}>
      <div
        className={styles.field}
        data-state={state}
        role={play.interactive ? "textbox" : undefined}
        tabIndex={play.interactive ? 0 : undefined}
        aria-label={play.revealed ? `Answer ${shownText} (correct)` : "Answer"}
        aria-describedby={play.interactive ? hintId : undefined}
        aria-readonly={!live || undefined}
        onKeyDown={play.interactive ? (e) => handleTypingKey(e, play, allowMinus) && e.preventDefault() : undefined}
        data-field="number"
      >
        <span className={styles.fieldLabel} aria-hidden>
          Answer
        </span>
        <span className={styles.fieldValue} aria-hidden={!play.interactive || undefined}>
          {value}
          {!play.revealed && live ? <span className={styles.caret} aria-hidden /> : null}
        </span>
        {allowMinus && play.interactive ? (
          <button type="button" className={styles.fieldButton} aria-label="Minus" aria-disabled={!live || undefined} onClick={() => live && play.type("-")}>
            ±
          </button>
        ) : null}
        {play.interactive ? (
          <button type="button" className={styles.fieldButton} aria-label="Delete" aria-disabled={!live || undefined} onClick={() => live && play.erase()} data-key="erase">
            <EraseIcon />
          </button>
        ) : (
          <span className={styles.fieldButton} aria-hidden>
            <EraseIcon />
          </span>
        )}
      </div>
      {play.interactive ? (
        <span id={hintId} className={styles.srOnly}>
          Type a whole number{play.maxDigits < 6 ? ` of up to ${play.maxDigits} digits` : ""}, or use the keypad.
        </span>
      ) : null}
      {wrong ? <p className={styles.yours}>You entered {play.text}</p> : null}
      {play.revealed ? null : <Keypad keys={DIGIT_KEYS} play={play} />}
    </div>
  );
}

/**
 * answer.digits@1 (board 17 M6b): one slot per digit, typed in order; the next slot has the accent ring,
 * the rest are dashed. Revealed, the slots hold the correct digits and the player's wrong ones are marked.
 */
function DigitsInput({ play }: { play: LaunchPlay }) {
  const n = play.maxDigits;
  const shown = play.shown?.kind === "digits" ? String(play.shown.value ?? "") : "";
  const live = play.interactive && !play.locked;
  const typed = play.text;
  const label = play.revealed
    ? `Digits, correct: ${[...shown].join(" ")}`
    : `Digits, ${typed.length} of ${n} entered${typed ? `: ${[...typed].join(" ")}` : ""}`;
  return (
    <div className={styles.answer}>
      <div
        className={styles.slots}
        style={{ "--slots": n } as CSSProperties}
        role={play.interactive ? "textbox" : "img"}
        tabIndex={play.interactive ? 0 : undefined}
        aria-label={label}
        aria-readonly={play.interactive ? !live || undefined : undefined}
        onKeyDown={play.interactive ? (e) => handleTypingKey(e, play, false) && e.preventDefault() : undefined}
        data-field="digits"
      >
        {Array.from({ length: n }, (_, i) => {
          const mine = typed[i] ?? "";
          const digit = play.revealed ? (shown[i] ?? "") : mine;
          const state = play.revealed
            ? play.interactive && mine && mine !== shown[i]
              ? "wrong"
              : "correct"
            : mine
              ? "filled"
              : i === typed.length && live
                ? "next"
                : "empty";
          return (
            <span key={i} className={styles.slot} data-state={state} aria-hidden>
              {digit}
            </span>
          );
        })}
      </div>
      {play.revealed ? null : <Keypad keys={DIGIT_KEYS} play={play} erase="tall" />}
    </div>
  );
}

/** A polite live line under the input: "3 / 8 tapped", "2 / 4 answered", "5 of 9 filled". */
function Status({ children, tone }: { children: ReactNode; tone?: "accent" }) {
  return (
    <p className={styles.status} data-tone={tone} aria-live="polite">
      {children}
    </p>
  );
}

/**
 * answer.fill@1 (board 14 N7–N9): the blanks live on the board (tap one, then type); here are the keypad,
 * limited to the values a blank can take, and the count.
 */
function FillInput({ play }: { play: LaunchPlay }) {
  const fill = play.fill!;
  const filled = fill.values.filter((v) => v !== null).length;
  const erase = fill.multiDigit ? "tall" : "inline";
  return (
    <div className={styles.answer}>
      {play.revealed ? (
        <Status tone="accent">Answers placed in the blanks</Status>
      ) : play.interactive ? (
        <Status>
          {filled} of {fill.cells.length} filled{filled === fill.cells.length ? "" : " · tap a blank, then type"}
        </Status>
      ) : null}
      {play.revealed ? null : <Keypad keys={fill.keys} play={play} erase={erase} minus={fill.negative} label="Keypad for the selected blank" />}
    </div>
  );
}

/**
 * answer.sequence@1 (board 17 M3b/M5b): taps happen on the board; here is the counter ("3 / 8 tapped"), or
 * with stopOnError "Next: 4 · a wrong tap ends the round", and Undo when taps can be taken back.
 */
function SequenceInput({ play }: { play: LaunchPlay }) {
  const seq = play.sequence!;
  const done = seq.taps.length;
  const live = play.interactive && !play.locked;
  if (play.revealed) return <Status tone="accent">The order is numbered on the board</Status>;
  if (!play.interactive) {
    return <Status>{seq.stopOnError ? `Tap ${seq.count} in order · a wrong tap ends the round` : `Tap ${seq.count} in order`}</Status>;
  }
  return (
    <div className={styles.statusRow}>
      <Status>
        {seq.stopOnError && done < seq.count ? `Next: ${done + 1} · a wrong tap ends the round` : `${done} / ${seq.count} tapped`}
      </Status>
      {!seq.stopOnError && done > 0 ? (
        <button type="button" className={styles.textButton} aria-disabled={!live || undefined} onClick={() => live && play.undo()}>
          Undo
        </button>
      ) : null}
    </div>
  );
}

/** Whether a rows answer draws its Yes/No beside the board's grid rows (board 18 A7) rather than as a list. */
export function rowsBesideGrid(board: Board | undefined, count: number): boolean {
  return kindOf(board) === "grid" && Math.trunc(Number((board as GridBoard).rows)) === count;
}

/** One row's Yes/No pair (a toggle each; Y and N keys answer the focused row). */
export function YesNo({ play, row, label }: { play: LaunchPlay; row: number; label: string }) {
  const rows = play.rows!;
  const mine = rows.value[row] ?? "_";
  const correct = rows.shown?.[row] ?? null;
  const live = play.interactive && !play.locked;
  const onKeyDown = (e: KeyboardEvent) => {
    const k = e.key.toUpperCase();
    if ((k === "Y" || k === "N") && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      play.setRow(row, k);
    }
  };
  return (
    <div className={styles.yesNo} role="group" aria-label={label} data-row={row}>
      {(["Y", "N"] as const).map((v) => {
        const picked = mine === v;
        const state =
          correct !== null
            ? correct === v
              ? "correct"
              : play.interactive && picked
                ? "wrong"
                : "idle"
            : picked
              ? "picked"
              : "idle";
        const text = v === "Y" ? "Yes" : "No";
        return play.interactive ? (
          <button
            key={v}
            type="button"
            className={styles.yn}
            data-state={state}
            data-yn={v}
            aria-pressed={picked}
            aria-label={`${text}${state === "correct" ? " (correct)" : ""}`}
            aria-disabled={!live || undefined}
            onClick={() => live && play.setRow(row, v)}
            onKeyDown={onKeyDown}
          >
            {text}
          </button>
        ) : (
          <span key={v} className={styles.yn} data-state={state} data-yn={v} aria-label={state === "correct" ? `${text} (correct)` : undefined}>
            {text}
          </span>
        );
      })}
    </div>
  );
}

/** answer.rows@1 (board 18 A7): the counter, plus the rows themselves when the board can't carry them. */
function RowsInput({ play, beside }: { play: LaunchPlay; beside: boolean }) {
  const rows = play.rows!;
  const done = [...rows.value].filter((ch) => ch !== "_").length;
  return (
    <div className={styles.answer}>
      {beside ? null : (
        <div className={styles.rowsList}>
          {Array.from({ length: rows.count }, (_, i) => (
            <div key={i} className={styles.rowsItem}>
              <span className={styles.rowsNo} aria-hidden>
                {String(i + 1).padStart(2, "0")}
              </span>
              <YesNo play={play} row={i} label={`Row ${i + 1}`} />
            </div>
          ))}
        </div>
      )}
      {play.revealed ? (
        <Status tone="accent">Answers marked on every row</Status>
      ) : play.interactive ? (
        <Status>
          {done} / {rows.count} answered
        </Status>
      ) : null}
    </div>
  );
}

/** The answer area for a launch answer kind. */
export function LaunchAnswerView({ play, board }: { play: LaunchPlay; board: Board | undefined }) {
  switch (play.kind) {
    case "number":
      return <NumberInput play={play} />;
    case "digits":
      return <DigitsInput play={play} />;
    case "fill":
      return <FillInput play={play} />;
    case "sequence":
      return <SequenceInput play={play} />;
    case "rows":
      return <RowsInput play={play} beside={rowsBesideGrid(board, play.rows!.count)} />;
  }
}

